import { css } from '@emotion/react';
import { FC } from 'react';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Country } from 'react-phone-number-input/input-core';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { CountryLogo } from '~src/country/country-logo';
import { useCountryNamesTranslations } from '~src/country/country-names';

import { VatFieldType, vatFieldTypes } from '~address-form/validation/vat-field-validation';

interface VatSelectProps {
  name: string;
  vatCode: VatFieldType;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const VatSelect: FC<VatSelectProps> = ({ name, onChange, vatCode }) => {
  const { tICU } = useTranslations();
  const { translate } = useCountryNamesTranslations();
  const { register } = useFormContext();

  const vatNameParts = vatCode?.split('_');

  return (
    <div css={styles.selectWrapper}>
      <select
        {...register(name)}
        css={styles.selectInput}
        value={vatCode}
        onChange={onChange}
        data-test="vat-country-select"
      >
        {vatFieldTypes.map(country =>
          country === 'other' ? (
            <option key="other" value="other">
              {tICU('widget.other')}
            </option>
          ) : (
            <option
              key={country}
              value={country}
              dangerouslySetInnerHTML={{
                __html: ` ${translate(country.split('_')[0])} ${country.split('_')[1]}`,
              }}
            />
          )
        )}
      </select>
      <div className="selectButton" css={styles.selectButton}>
        {vatNameParts.length > 1 ? (
          <>
            <CountryLogo countryCode={vatNameParts[0] as Country} height={16} />
            <span css={styles.vatType}>{vatNameParts[1]}</span>
          </>
        ) : (
          <span>{tICU('widget.other')}</span>
        )}
        <div className="selectBorder" css={styles.selectBorder} />
      </div>
    </div>
  );
};

const styles = {
  selectInput: css({
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    zIndex: 1,
    opacity: 0,
    cursor: 'pointer',
    '&.focus-visible + .selectButton > .selectBorder, &:focus + .selectButton > .selectBorder': {
      border: '2px dotted black',
    },
  }),
  selectBorder: css({
    position: 'absolute',
    top: 0,
    left: 0,
    height: '70%',
    width: '100%',
    transform: 'translateY(20%)',
  }),
  selectButton: css({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  }),
  selectWrapper: css({
    position: 'relative',
    alignSelf: 'stretch',
    display: 'flex',
    paddingLeft: '16px',
    paddingTop: '18px',
  }),
  vatType: {
    marginLeft: '4px',
  },
};
